import { useField } from "formik";
import type { ComponentProps } from "react";
import { FormField } from "@/components/common/form-field";
import {
  formatDate,
  formatEIN,
  formatPhone,
  formatSSN,
  formatTime,
  formatZip,
} from "@/lib/masks";

type FormFieldProps = ComponentProps<typeof FormField>;

interface MaskedFormFieldProps extends Omit<FormFieldProps, "onChange"> {
  /** Formats the raw input into the value stored in Formik (and displayed). */
  format: (raw: string) => string;
}

/**
 * A Formik-bound input that reformats its value on every keystroke. Built on
 * `FormField`, which spreads `{...field}` before `{...props}` — so passing
 * `value`/`onChange` here overrides Formik's defaults while `onBlur` and error
 * rendering still come from `FormField`. The stored value is the *formatted*
 * string; formatters strip non-digits so typing is effectively numeric-only.
 *
 * Note: reformatting rewrites the value, so the caret jumps to the end on
 * mid-string edits — fine for normal left-to-right entry.
 */
export function MaskedFormField({ name, format, ...props }: MaskedFormFieldProps) {
  const [field, , helpers] = useField<string>(name);

  return (
    <FormField
      name={name}
      inputMode="numeric"
      autoComplete="off"
      {...props}
      value={field.value ?? ""}
      onChange={(e) => helpers.setValue(format(e.target.value))}
    />
  );
}

type FieldProps = Omit<MaskedFormFieldProps, "format">;

// `format` is spread AFTER `{...props}` so each field's mask is locked in — a
// caller can override maxLength/placeholder/etc. but never the formatter that
// the matching Yup regex depends on.

/** SSN/ITIN — `###-##-####`. Sensitive: never logged/persisted (Formik state only). */
export function SsnField(props: FieldProps) {
  return <MaskedFormField maxLength={11} placeholder="123-45-6789" {...props} format={formatSSN} />;
}

/** EIN — `##-#######`. Sensitive: never logged/persisted. */
export function EinField(props: FieldProps) {
  return <MaskedFormField maxLength={10} placeholder="12-3456789" {...props} format={formatEIN} />;
}

/** US phone — `(###) ###-####`. */
export function PhoneField(props: FieldProps) {
  return (
    <MaskedFormField
      inputMode="tel"
      maxLength={14}
      placeholder="(123) 456-7890"
      {...props}
      format={formatPhone}
    />
  );
}

/** US ZIP / ZIP+4 — `#####` or `#####-####`. */
export function ZipField(props: FieldProps) {
  return <MaskedFormField maxLength={10} placeholder="12345" {...props} format={formatZip} />;
}

/** Date — `MM/DD/YYYY`. */
export function DateField(props: FieldProps) {
  return <MaskedFormField maxLength={10} placeholder="MM/DD/YYYY" {...props} format={formatDate} />;
}

/** Time — `HH:MM` (24-hour). */
export function TimeField(props: FieldProps) {
  return <MaskedFormField maxLength={5} placeholder="HH:MM" {...props} format={formatTime} />;
}
